/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { css, jsx, keyframes } from '@compiled/react';

import { cssMap } from '@atlaskit/css';

import type { IconComponent } from './types';

const ICON_CYCLE_DELAY_MS = 900;
const ICON_CYCLE_TRANSITION_MS = 400;
const ICON_CYCLE_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

interface IconCyclerProps {
	animationDisabled?: boolean;
	cycleDirection?: 'left' | 'right';
	icons: IconComponent[];
	testId?: string;
}

const enteringAnimations = cssMap({
	left: {
		position: 'relative',
		animationName: keyframes({
			from: { transform: 'translateX(100%)', opacity: 0 },
			to: { transform: 'translateX(0)', opacity: 1 },
		}),
	},
	right: {
		position: 'relative',
		animationName: keyframes({
			from: { transform: 'translateX(-100%)', opacity: 0 },
			to: { transform: 'translateX(0)', opacity: 1 },
		}),
	},
});

const exitingAnimations = cssMap({
	left: {
		position: 'absolute',
		animationName: keyframes({
			from: { transform: 'translateX(0)', opacity: 1 },
			to: { transform: 'translateX(-100%)', opacity: 0 },
		}),
	},
	right: {
		position: 'absolute',
		animationName: keyframes({
			from: { transform: 'translateX(0)', opacity: 1 },
			to: { transform: 'translateX(100%)', opacity: 0 },
		}),
	},
});

const commonAnimationStyles = css({
	animationDuration: `${ICON_CYCLE_TRANSITION_MS}ms`,
	animationTimingFunction: ICON_CYCLE_EASING,
	animationFillMode: 'forwards',
});

const containerStyles = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const iconSlotStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

/**
 * Cycles through a list of icon components with a vertical slide transition.
 *
 * Each icon is shown for ICON_CYCLE_DELAY_MS, then the current icon slides
 * out upward while the next icon slides in from below over ICON_CYCLE_TRANSITION_MS.
 */
export default function IconCycler({
	animationDisabled = false,
	icons,
	cycleDirection = 'left',
	testId,
}: IconCyclerProps): JSX.Element | null {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const nextIndex = (currentIndex + 1) % icons.length;

	const scheduleNextTransition = useCallback(() => {
		timerRef.current = setTimeout(() => {
			setIsTransitioning(true);
		}, ICON_CYCLE_DELAY_MS);
	}, []);

	const handleAnimationEnd = useCallback(() => {
		setIsTransitioning(false);
		setCurrentIndex((prev) => (prev + 1) % icons.length);
	}, [icons.length]);

	useEffect(() => {
		if (animationDisabled || icons.length <= 1) {
			return;
		}

		if (!isTransitioning) {
			scheduleNextTransition();
		}

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [animationDisabled, icons.length, isTransitioning, scheduleNextTransition]);

	if (icons.length === 0) {
		return null;
	}

	const CurrentIcon = icons[currentIndex];
	const NextIcon = icons[nextIndex];

	if (animationDisabled || icons.length === 1 || !isTransitioning) {
		return (
			<div css={containerStyles} data-testid={testId}>
				<div css={iconSlotStyles}>
					<CurrentIcon label="" />
				</div>
			</div>
		);
	}

	return (
		<div css={containerStyles} data-testid={testId}>
			<div css={[iconSlotStyles, commonAnimationStyles, exitingAnimations[cycleDirection]]}>
				<CurrentIcon label="" />
			</div>
			<div
				css={[iconSlotStyles, commonAnimationStyles, enteringAnimations[cycleDirection]]}
				onAnimationEnd={handleAnimationEnd}
			>
				<NextIcon label="" />
			</div>
		</div>
	);
}
